import { Card } from '@/components/ui/card';

export const TermsOfService = () => {
  return (
    <div className="min-h-screen pt-44 pb-16 bg-slate-50" data-testid="terms-page">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <Card className="p-8 md:p-12">
          <h1 className="font-outfit text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Effective Date: January 2025</p>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                By accessing or using BidZuu (website or mobile app), you agree to be bound by these Terms of Service. If you do not agree, you may not use the platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Platform Description</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                BidZuu is a community-driven auction and marketplace platform that allows users to:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>List items or services for auction or sale</li>
                <li>Place bids on listed items</li>
                <li>Communicate with other users</li>
                <li>Arrange delivery or local pickup</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                BidZuu acts as a technology platform only and does not own, buy, or sell items directly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Users must provide accurate and truthful information</li>
                <li>You are responsible for maintaining the security of your account</li>
                <li>One person may not operate multiple fraudulent accounts</li>
                <li>BidZuu may suspend or terminate accounts for violations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Listings & Auctions</h2>
              <p className="text-slate-600 leading-relaxed mb-4">Sellers are responsible for:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Accuracy of listings</li>
                <li>Legal ownership of items</li>
                <li>Item condition and descriptions</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Prohibited items are not allowed (illegal goods, weapons, drugs, counterfeit items, adult content).
              </p>
              <p className="text-slate-600 leading-relaxed mt-2">
                BidZuu reserves the right to remove listings without notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Fees</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>BidZuu charges a flat <strong>10% commission</strong> on successful sales</li>
                <li>No hidden fees</li>
                <li>Fees are deducted automatically after a completed transaction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Bids & Transactions</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Bids are binding</li>
                <li>Sellers may accept or reject bids according to platform rules</li>
                <li>Buyers must complete payment once a bid is accepted</li>
                <li>Failure to complete a transaction may result in account penalties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Payments & Escrow</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Payments may be processed through third-party providers</li>
                <li>BidZuu may use escrow-style holding to protect buyers and sellers</li>
                <li>BidZuu is not a bank or financial institution</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Reviews & Ratings</h2>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Reviews must be honest and respectful</li>
                <li>Fake or abusive reviews are prohibited</li>
                <li>BidZuu may remove reviews that violate policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed mb-4">BidZuu is not responsible for:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Item quality</li>
                <li>Delivery delays</li>
                <li>Disputes between users</li>
                <li>Losses caused by user misconduct</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4 font-medium">
                Use of the platform is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
              <p className="text-slate-600 leading-relaxed mb-4">BidZuu may suspend or terminate accounts:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>For policy violations</li>
                <li>Fraud or abuse</li>
                <li>Legal or regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                BidZuu may update these Terms at any time. Continued use means acceptance of updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
              <p className="text-slate-600 leading-relaxed">
                📧 <a href="mailto:support@bidzuu.com" className="text-emerald-600 hover:underline">support@bidzuu.com</a>
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-44 pb-16 bg-slate-50" data-testid="privacy-page">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <Card className="p-8 md:p-12">
          <h1 className="font-outfit text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Effective Date: January 2025</p>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We may collect:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Name, email, phone number</li>
                <li>Account credentials</li>
                <li>Listings, bids, messages</li>
                <li>Location data (optional)</li>
                <li>Device and usage data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Information</h2>
              <p className="text-slate-600 leading-relaxed mb-4">Your information is used to:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Operate the BidZuu platform</li>
                <li>Process transactions</li>
                <li>Provide customer support</li>
                <li>Prevent fraud</li>
                <li>Improve user experience</li>
                <li>Send notifications (optional)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Sharing of Information</h2>
              <p className="text-slate-600 leading-relaxed mb-4 font-medium">We do not sell your data.</p>
              <p className="text-slate-600 leading-relaxed mb-4">We may share information with:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Payment processors</li>
                <li>Identity verification providers</li>
                <li>Shipping partners</li>
                <li>Legal authorities if required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Identity Verification</h2>
              <p className="text-slate-600 leading-relaxed mb-4">To protect the community, BidZuu may request:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Government-issued ID</li>
                <li>Phone or email verification</li>
                <li>Additional verification for high-value transactions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Cookies & Tracking</h2>
              <p className="text-slate-600 leading-relaxed mb-4">BidZuu uses cookies and analytics tools to:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Improve performance</li>
                <li>Understand usage patterns</li>
                <li>Enhance security</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                You can disable cookies in your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Advertising & Third-Party Services</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                BidZuu uses Google AdSense and other advertising partners to display ads on our platform. These services may use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Serve personalized ads based on your interests</li>
                <li>Measure ad performance and effectiveness</li>
                <li>Limit the number of times you see an ad</li>
                <li>Understand user interaction with advertisements</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                <strong>Google AdSense:</strong> Google uses cookies to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Google Ads Settings</a>.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                <strong>Third-Party Vendors:</strong> Third-party vendors, including Google, use cookies to serve ads based on your prior visits. You may opt out of third-party vendor's use of cookies for personalized advertising by visiting{' '}
                <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">www.aboutads.info</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Data Security</h2>
              <p className="text-slate-600 leading-relaxed">
                We use industry-standard measures to protect data, but no system is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. User Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Access your data</li>
                <li>Update your information</li>
                <li>Request account deletion</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed mb-4">Some data may be retained for:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Legal compliance</li>
                <li>Fraud prevention</li>
                <li>Financial records</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed">
                BidZuu is not intended for users under 18.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Policy Updates</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy. Changes will be posted on the platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
              <p className="text-slate-600 leading-relaxed">
                📧 <a href="mailto:privacy@bidzuu.com" className="text-emerald-600 hover:underline">privacy@bidzuu.com</a>
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default { TermsOfService, PrivacyPolicy };
